import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { Badge, Button, EmptyState, ErrorText, Field, Input, Select, Spinner } from '../../components/ui'
import { Icon } from '../../components/ui/icons'
import { CERTAINTY_LABELS, LATERALITY_LABELS } from '../../utils/format'
import type { DiagnosisCertainty, EncounterDiagnosis, IcdCode, Laterality } from '../../types/api'

/**
 * Diagnósticos CIE-10 de la atención (H3). Buscador sobre el catálogo +
 * condición diagnóstica (hipótesis/confirmado) y lateralidad, que en
 * traumatología distingue dos patologías distintas.
 */

/** Espera antes de consultar el catálogo, para no pedir en cada tecla. */
const SEARCH_DEBOUNCE_MS = 250

function useDebounced(value: string, ms: number) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return debounced
}

export function DiagnosesSection({ encounterId, readOnly }: { encounterId: string; readOnly: boolean }) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<IcdCode | null>(null)
  const [certainty, setCertainty] = useState<DiagnosisCertainty>('HYPOTHESIS')
  const [laterality, setLaterality] = useState<Laterality>('NOT_APPLICABLE')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')
  const debounced = useDebounced(search, SEARCH_DEBOUNCE_MS)

  const diagnoses = useQuery({
    queryKey: ['diagnoses', encounterId],
    queryFn: async () => (await api.get<EncounterDiagnosis[]>(`/encounters/${encounterId}/diagnoses`)).data,
  })

  const catalog = useQuery({
    queryKey: ['icd', debounced],
    queryFn: async () => (await api.get<IcdCode[]>('/icd', { params: { q: debounced, limit: 12 } })).data,
    enabled: debounced.trim().length >= 2 && !readOnly,
  })

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['diagnoses', encounterId] })
    setError('')
  }

  const add = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/encounters/${encounterId}/diagnoses`, {
          icdCode: selected!.code,
          certainty,
          laterality,
          notes: notes || null,
          isPrimary: (diagnoses.data ?? []).length === 0, // el primero es el principal
        })
      ).data,
    onSuccess: () => {
      setSelected(null)
      setSearch('')
      setNotes('')
      setLaterality('NOT_APPLICABLE')
      setCertainty('HYPOTHESIS')
      refresh()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<EncounterDiagnosis> }) =>
      (await api.patch(`/encounters/${encounterId}/diagnoses/${id}`, patch)).data,
    onSuccess: refresh,
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const remove = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/encounters/${encounterId}/diagnoses/${id}`)).data,
    onSuccess: refresh,
    onError: (err) => setError(apiErrorMessage(err)),
  })

  return (
    <div className="space-y-4">
      {diagnoses.isLoading ? (
        <Spinner />
      ) : (diagnoses.data ?? []).length === 0 ? (
        <EmptyState message="Sin diagnósticos registrados en esta atención." />
      ) : (
        <ul className="space-y-2">
          {(diagnoses.data ?? []).map((d) => (
            <li key={d.id} className="rounded-lg border border-steel-100 p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-ink">
                    <span className="font-mono text-brand-700">{d.icdCode}</span> · {d.description}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {d.isPrimary && <Badge className="bg-brand-200 text-brand-800">Principal</Badge>}
                    <Badge className={d.certainty === 'CONFIRMED' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                      {CERTAINTY_LABELS[d.certainty]}
                    </Badge>
                    {d.laterality !== 'NOT_APPLICABLE' && (
                      <Badge className="bg-steel-100 text-steel-700">{LATERALITY_LABELS[d.laterality]}</Badge>
                    )}
                  </div>
                  {d.notes && <p className="mt-1 text-sm text-steel-600">{d.notes}</p>}
                </div>
                {!readOnly && (
                  <div className="flex shrink-0 items-center gap-1.5">
                    {d.certainty === 'HYPOTHESIS' && (
                      <Button variant="secondary" onClick={() => update.mutate({ id: d.id, patch: { certainty: 'CONFIRMED' } })}>
                        Confirmar
                      </Button>
                    )}
                    {!d.isPrimary && (
                      <Button variant="ghost" onClick={() => update.mutate({ id: d.id, patch: { isPrimary: true } })}>
                        Marcar principal
                      </Button>
                    )}
                    <Button variant="ghost" aria-label="Quitar diagnóstico" onClick={() => remove.mutate(d.id)}>
                      <Icon name="close" className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <div className="space-y-3 rounded-lg border border-steel-100 bg-surface p-3">
          <Field label="Buscar en el catálogo CIE-10" hint="Por código o por texto: «esguince tobillo», «M54.5», «lumbago».">
            <Input
              value={selected ? `${selected.code} · ${selected.description}` : search}
              onChange={(e) => {
                setSelected(null)
                setSearch(e.target.value)
              }}
              placeholder="Escribe al menos 2 caracteres…"
            />
          </Field>

          {!selected && debounced.trim().length >= 2 && (
            <div className="max-h-56 overflow-y-auto rounded-lg border border-steel-100 bg-white">
              {catalog.isLoading ? (
                <div className="p-3"><Spinner /></div>
              ) : (catalog.data ?? []).length === 0 ? (
                <p className="p-3 text-sm text-steel-500">Sin resultados en el catálogo cargado.</p>
              ) : (
                <ul className="divide-y divide-steel-100">
                  {(catalog.data ?? []).map((c) => (
                    <li key={c.code}>
                      <button
                        type="button"
                        onClick={() => setSelected(c)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-soft"
                      >
                        <span className="font-mono font-semibold text-brand-700">{c.code}</span> · {c.description}
                        {c.chapter && <span className="block text-xs text-steel-500">{c.chapter}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {selected && (
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Condición diagnóstica">
                <Select value={certainty} onChange={(e) => setCertainty(e.target.value as DiagnosisCertainty)}>
                  {Object.entries(CERTAINTY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Lateralidad">
                <Select value={laterality} onChange={(e) => setLaterality(e.target.value as Laterality)}>
                  {Object.entries(LATERALITY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Fundamento (opcional)">
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="p. ej. Lachman positivo" />
              </Field>
            </div>
          )}

          <Button variant="cta" disabled={!selected || add.isPending} onClick={() => add.mutate()}>
            {add.isPending ? 'Agregando…' : 'Agregar diagnóstico'}
          </Button>
          <p className="text-xs text-steel-500">
            Catálogo acotado a códigos frecuentes de traumatología, columna, deporte y salud ocupacional.
            [TODO] Cargar el catálogo CIE-10 oficial completo.
          </p>
        </div>
      )}

      <ErrorText>{error}</ErrorText>
    </div>
  )
}
